import { createSelector } from 'reselect';

import { BULK_RECIPIENTS_THRESHOLD } from '../../../constants/createSendRecipients';
import { IOrgAddress } from '../../../types/addressBook';
import {
  CreateSendMethodEnum,
  CreateSendRecipientsModeEnum,
  TCreateSendRecipientsMode,
} from '../../../types/createSendFlow';
import { ICreateSendRecipient, IRecipientsGate, ShippingModeEnum } from '../../../types/createSendRecipients';
import {
  isRecipientComplete,
  isRecipientMissingPhone,
  isRecipientMissingShippingAddress,
  isRecipientPhoneRequired,
  resolveEffectiveShipping,
} from '../../../utils/createSendRecipients';
import { IAppState } from '../../reducers';
import { selectAddressBook } from '../../selectors/addressBook';
import {
  selectCsHasGiftBudget,
  selectCsHasMarketplaceContent,
  selectCsHasMskuContent,
  selectCsIsBusinessReasonRequiredByCampaign,
  selectCsIsContentDigital,
  selectCsKitIds,
} from './contents';
import { selectCsCrmByRecipientUid, selectIsCsCrmRequiredForMethod, selectIsCsCrmRequirementUnresolved } from './crm';
import { selectCreateSendMethod } from './method';
import { selectIsCsBulkSendAllowed } from './permissions';
import {
  selectCsBusinessReasonId,
  selectCsIsOfficeDropshipEnabled,
  selectIsCsReleaseDateMissing,
} from './sendSettings';

export const selectCsRecipientsState = (state: IAppState) => state.createSend.recipients;

export const selectCsRecipients = createSelector(selectCsRecipientsState, (state) => state.recipients);

export const selectCsAddressMode = createSelector(selectCsRecipientsState, (state) => state.addressMode);

export const selectCsClaimLinkMode = createSelector(selectCsRecipientsState, (state) => state.claimLinkMode);

export const selectCsMultiKitAddressMode = createSelector(
  selectCsRecipientsState,
  (state) => state.multiKitAddressMode,
);

export const selectCsOverrideAddress = createSelector(selectCsRecipientsState, (state) => state.overrideAddress);

export const selectCsOneTimeAddress = createSelector(selectCsRecipientsState, (state) => state.oneTimeAddress);

export const selectCsClaimAddressIds = createSelector(
  selectCsRecipientsState,
  (state) => state.selectedClaimAddressIds,
);

export const selectHasOverrideAddress = createSelector(selectCsOverrideAddress, Boolean);

export const selectCsSelectedClaimAddresses = createSelector(
  selectCsClaimAddressIds,
  selectAddressBook,
  (ids, book): IOrgAddress[] => {
    const addresses = book ?? [];

    return ids.reduce<IOrgAddress[]>((acc, uid) => {
      const match = addresses.find((address) => address.uid === uid);

      return match ? [...acc, match] : acc;
    }, []);
  },
);

export const selectCsNeedsAddressBookForClaimAddresses = createSelector(
  selectCsClaimAddressIds,
  selectAddressBook,
  (ids, book): boolean => ids.length > 0 && !book,
);

export const selectKitsCount = createSelector(selectCsKitIds, (ids) => ids?.length ?? 0);

export const selectIsMultiKit = createSelector(selectKitsCount, (count) => count >= 2);

/**
 * The claim link is forced ON when the recipient — not the sender — must choose or supply an
 * address at claim time: MSKU variants, marketplace content (always forced in v1, even for a
 * single variant — claim-less is post-v1, EX-6382), or a gift budget. This only widens the
 * forced-ON set; the base claim-link rule is unchanged.
 */
export const selectIsClaimForced = createSelector(
  selectCsHasMskuContent,
  selectCsHasMarketplaceContent,
  selectCsHasGiftBudget,
  (hasMsku, hasMarketplace, hasGiftBudget): boolean => hasMsku || hasMarketplace || hasGiftBudget,
);

export const selectUsesClaimAddressModes = createSelector(
  selectIsMultiKit,
  selectIsClaimForced,
  (isMultiKit, isClaimForced): boolean => isMultiKit || isClaimForced,
);

export const selectEffectiveShipping = createSelector(
  selectIsMultiKit,
  selectCsAddressMode,
  selectCsClaimLinkMode,
  selectCsMultiKitAddressMode,
  selectIsClaimForced,
  selectCsIsContentDigital,
  resolveEffectiveShipping,
);

export const selectIsClaimBased = createSelector(selectEffectiveShipping, (effective) => effective.isClaimBased);

export const selectIsCsRecipientPhoneRequired = createSelector(
  selectCsIsContentDigital,
  selectIsClaimBased,
  isRecipientPhoneRequired,
);

export const selectCsIsGroupShipmentApplicable = createSelector(
  selectCreateSendMethod,
  selectEffectiveShipping,
  selectCsMultiKitAddressMode,
  selectCsOverrideAddress,
  selectCsClaimAddressIds,
  (method, effective, multiKitMode, overrideAddress, claimAddressIds): boolean => {
    const mode = method === CreateSendMethodEnum.Link ? multiKitMode : effective.mode;

    return mode === ShippingModeEnum.FixedAddress || mode === ShippingModeEnum.YouProvide
      ? Boolean(overrideAddress)
      : claimAddressIds.length > 0;
  },
);

export const selectCsIsGroupShipmentEnabled = createSelector(
  selectCsIsGroupShipmentApplicable,
  selectCsIsOfficeDropshipEnabled,
  (isApplicable, isEnabled): boolean => isApplicable && Boolean(isEnabled),
);

export const selectShowOverrideAddress = createSelector(
  selectCsAddressMode,
  selectCsRecipients,
  (addressMode, recipients) => addressMode === ShippingModeEnum.YouProvide && recipients.length >= 2,
);

export const selectCompleteRecipients = createSelector(selectCsRecipients, (recipients): ICreateSendRecipient[] =>
  recipients.filter(isRecipientComplete),
);

export const selectCompleteRecipientsCount = createSelector(
  selectCompleteRecipients,
  (recipients) => recipients.length,
);

export const selectCsRecipientsMode = createSelector(
  selectCompleteRecipientsCount,
  (count): TCreateSendRecipientsMode | null => {
    if (count >= BULK_RECIPIENTS_THRESHOLD) {
      return CreateSendRecipientsModeEnum.Many;
    }
    if (count === 1) {
      return CreateSendRecipientsModeEnum.One;
    }
    return null;
  },
);

export const selectIsCsCrmRequired = createSelector(
  selectIsCsCrmRequiredForMethod,
  selectCsRecipients,
  (isRequiredForMethod, recipients): boolean => isRequiredForMethod && recipients.length <= 1,
);

export const selectIsCsRecipientCrmLinkMissing = createSelector(
  selectIsCsCrmRequired,
  selectCsRecipients,
  selectCsCrmByRecipientUid,
  (isRequired, recipients, byUid): boolean => isRequired && recipients.some(({ uid }) => !byUid[uid]),
);

export const selectRecipientsGate = createSelector(
  selectEffectiveShipping,
  selectCsClaimAddressIds,
  selectCsRecipients,
  selectHasOverrideAddress,
  selectCsIsContentDigital,
  selectCsIsBusinessReasonRequiredByCampaign,
  selectCsBusinessReasonId,
  selectIsCsRecipientCrmLinkMissing,
  selectIsCsCrmRequirementUnresolved,
  selectIsCsReleaseDateMissing,
  selectIsCsBulkSendAllowed,
  selectIsCsRecipientPhoneRequired,
  (
    effective,
    claimAddressIds,
    recipients,
    hasOverride,
    isContentDigital,
    isBusinessReasonRequired,
    businessReasonId,
    isCrmLinkMissing,
    isCrmRequirementUnresolved,
    isReleaseDateMissing,
    isBulkSendAllowed,
    isPhoneRequired,
  ): IRecipientsGate => {
    if (recipients.length === 0) {
      return { valid: false, reason: 'no-recipients' };
    }

    if (!isBulkSendAllowed && recipients.length > 1) {
      return { valid: false, reason: 'bulk-requires-admin' };
    }

    if (recipients.some((recipient) => !isRecipientComplete(recipient))) {
      return { valid: false, reason: 'incomplete-recipient' };
    }

    if (isCrmRequirementUnresolved) {
      return { valid: false, reason: 'crm-status-unknown' };
    }

    if (isCrmLinkMissing) {
      return { valid: false, reason: 'crm-record-required' };
    }

    if (isBusinessReasonRequired && !businessReasonId) {
      return { valid: false, reason: 'business-reason-required' };
    }

    if (isContentDigital) {
      return { valid: true, reason: null };
    }

    if (
      (effective.mode === ShippingModeEnum.YouProvide || effective.mode === ShippingModeEnum.FixedAddress) &&
      recipients.some((recipient) =>
        isRecipientMissingShippingAddress(recipient, { addressMode: ShippingModeEnum.YouProvide, hasOverride }),
      )
    ) {
      return { valid: false, reason: 'incomplete-recipient' };
    }

    if (recipients.some((recipient) => isRecipientMissingPhone(recipient, isPhoneRequired))) {
      return { valid: false, reason: 'incomplete-recipient' };
    }

    if (effective.mode === ShippingModeEnum.FromList && claimAddressIds.length < 2) {
      return { valid: false, reason: 'from-list-needs-2' };
    }

    if (isReleaseDateMissing) {
      return { valid: false, reason: 'release-date-required' };
    }

    return { valid: true, reason: null };
  },
);
